import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Search,
  Filter,
  Plus,
  Edit2,
  Trash2,
  X,
} from 'lucide-react';
import {
  AdminClientApi,
  ClientCouponInterface,
  GetCouponsInputInterface,
  CreateCouponInputInterface,
} from '../../Admin-ClientLibrary';
import { getApiUrl } from '../../config/api';
import { createCouponSchema, CreateCouponFormData } from '../../schemas/coupon.schema';
import './Coupons.css';

const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

const Coupons: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [coupons, setCoupons] = useState<ClientCouponInterface[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>(searchParams.get('search') || '');
  const [totalCoupons, setTotalCoupons] = useState<number>(0);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [couponToDelete, setCouponToDelete] = useState<ClientCouponInterface | null>(null);
  const [createLoading, setCreateLoading] = useState<boolean>(false);

  const [filters, setFilters] = useState({
    is_active: searchParams.get('is_active') || '',
    duration: searchParams.get('duration') || '',
  });

  // React Hook Form for Create Coupon
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<CreateCouponFormData>({
    resolver: zodResolver(createCouponSchema),
    defaultValues: {
      name: '',
      discountType: 'percent',
      percent_off: undefined,
      amount_off: undefined,
      currency: 'usd',
      duration: 'once',
      duration_in_months: undefined,
      max_redemptions: undefined,
      valid_until: undefined,
    },
  });

  const discountType = watch('discountType');
  const duration = watch('duration');

  const limit = 20;

  const updateUrlParams = (params: Record<string, string | number | undefined>): void => {
    const newParams = new URLSearchParams();
    searchParams.forEach((value, key) => {
      newParams.set(key, value);
    });
    Object.entries(params).forEach(([key, value]) => {
      if (value && value !== '') {
        newParams.set(key, value.toString());
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  const fetchCoupons = async (
    search: string = '',
    filterParams = filters,
    shouldGetCount: boolean = false
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      const apiParams: GetCouponsInputInterface = {
        limit,
        search,
        getCount: shouldGetCount,
      };

      if (filterParams.is_active) {
        apiParams.is_active = filterParams.is_active === 'true';
      }
      if (filterParams.duration) {
        apiParams.duration = filterParams.duration;
      }

      const response = await adminApi.getCoupons(apiParams);
      setCoupons(response.coupons);

      if (shouldGetCount && response.total !== undefined) {
        setTotalCoupons(response.total);
      }
    } catch (err: any) {
      console.error('Error fetching coupons:', err);
      setError(err.message || 'Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    const urlFilters = {
      is_active: searchParams.get('is_active') || '',
      duration: searchParams.get('duration') || '',
    };

    setSearchTerm(urlSearch);
    setFilters(urlFilters);
    setCurrentPage(1);

    fetchCoupons(urlSearch, urlFilters, true);
  }, [searchParams]);

  useEffect(() => {
    const state = location.state as { refresh?: boolean } | null;
    if (state?.refresh) {
      fetchCoupons(searchTerm, filters, true);
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.state]);

  const handleSearch = (e: React.FormEvent): void => {
    e.preventDefault();
    updateUrlParams({
      search: searchTerm,
      is_active: filters.is_active,
      duration: filters.duration,
    });
  };

  const handleFilterChange = (filterName: string, value: string): void => {
    setFilters({ ...filters, [filterName]: value });
  };

  const applyFilters = (): void => {
    updateUrlParams({
      search: searchTerm,
      is_active: filters.is_active,
      duration: filters.duration,
    });
    setShowFilters(false);
  };

  const clearFilters = (): void => {
    updateUrlParams({
      is_active: '',
      duration: '',
    });
    setShowFilters(false);
  };

  const formatDiscount = (coupon: ClientCouponInterface): string => {
    if (coupon.percent_off) {
      return `${coupon.percent_off}%`;
    }
    if (coupon.amount_off) {
      return `$${(coupon.amount_off / 100).toFixed(2)}`;
    }
    return '-';
  };

  const formatDuration = (coupon: ClientCouponInterface): string => {
    if (coupon.duration === 'once') return 'Once';
    if (coupon.duration === 'forever') return 'Forever';
    if (coupon.duration === 'repeating') {
      return `${coupon.duration_in_months} month${coupon.duration_in_months !== 1 ? 's' : ''}`;
    }
    return coupon.duration;
  };

  const formatDate = (date: Date | undefined): string => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  const handleRowClick = (couponId: number, event: React.MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.closest('button') || target.closest('.actions')) {
      return;
    }
    navigate(`/coupons/${couponId}`);
  };

  const onCreateCoupon = async (data: CreateCouponFormData): Promise<void> => {
    try {
      setCreateLoading(true);
      
      const couponData: CreateCouponInputInterface = {
        name: data.name,
        duration: data.duration,
      };

      if (data.duration === 'repeating' && data.duration_in_months) {
        couponData.duration_in_months = data.duration_in_months;
      }

      if (data.discountType === 'percent' && data.percent_off) {
        couponData.percent_off = data.percent_off;
      } else if (data.discountType === 'amount' && data.amount_off) {
        couponData.amount_off = data.amount_off;
        couponData.currency = data.currency || 'usd';
      }

      if (data.max_redemptions) {
        couponData.max_redemptions = data.max_redemptions;
      }

      if (data.valid_until) {
        couponData.valid_until = data.valid_until;
      }

      const response = await adminApi.createCoupon(couponData);

      if (response.success) {
        setShowCreateModal(false);
        reset();
        fetchCoupons(searchTerm, filters, true);
      } else {
        alert(response.message || 'Failed to create coupon');
      }
    } catch (err: any) {
      console.error('Error creating coupon:', err);
      alert(err.message || 'Failed to create coupon');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCloseCreateModal = (): void => {
    setShowCreateModal(false);
    reset();
  };

  const handleDeleteClick = (coupon: ClientCouponInterface, event: React.MouseEvent): void => {
    event.stopPropagation();
    setCouponToDelete(coupon);
    setShowDeleteModal(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!couponToDelete) return;

    try {
      const response = await adminApi.deleteCoupon({ id: couponToDelete.id });

      if (response.success) {
        setShowDeleteModal(false);
        setCouponToDelete(null);
        fetchCoupons(searchTerm, filters, true);
      } else {
        alert(response.message || 'Failed to delete coupon');
      }
    } catch (err: any) {
      console.error('Error deleting coupon:', err);
      alert(err.message || 'Failed to delete coupon');
    }
  };

  const totalPages = totalCoupons > 0 ? Math.ceil(totalCoupons / limit) : 1;

  const getFilterSummary = (): string => {
    const parts: string[] = [];
    
    if (filters.is_active) {
      parts.push(`Status: ${filters.is_active === 'true' ? 'Active' : 'Inactive'}`);
    }
    if (filters.duration) {
      parts.push(`Duration: ${filters.duration}`);
    }

    if (parts.length === 0) {
      return `Total: ${totalCoupons} coupon${totalCoupons !== 1 ? 's' : ''}`;
    }

    return `${parts.join(', ')} | Total: ${totalCoupons}`;
  };

  return (
    <div className="coupons-page">
      <div className="coupons-header">
        <div className="header-top">
          <h1>Coupons</h1>
          <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={16} />
            Create Coupon
          </button>
        </div>

        <div className="coupons-controls">
          <div className="controls-row">
            <form onSubmit={handleSearch} className="search-form">
              <div className="search-input">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search coupons..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button type="submit" className="btn-secondary">
                Search
              </button>
            </form>

            <button className="btn-secondary" onClick={() => setShowFilters(!showFilters)}>
              <Filter size={16} />
              Filter{' '}
              {(filters.is_active || filters.duration) && (
                <span className="filter-badge">•</span>
              )}
            </button>
          </div>

          <div className="filter-summary">{getFilterSummary()}</div>

          {showFilters && (
            <div className="filters-dropdown">
              <div className="filters-header">
                <h3>Filters</h3>
                <button className="btn-text" onClick={clearFilters}>
                  Clear All
                </button>
              </div>

              <div className="filter-group">
                <label>Status:</label>
                <select
                  value={filters.is_active}
                  onChange={(e) => handleFilterChange('is_active', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Duration:</label>
                <select
                  value={filters.duration}
                  onChange={(e) => handleFilterChange('duration', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="once">Once</option>
                  <option value="repeating">Repeating</option>
                  <option value="forever">Forever</option>
                </select>
              </div>

              <div className="filters-actions">
                <button className="btn-secondary" onClick={() => setShowFilters(false)}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={applyFilters}>
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="coupons-content">
        {error && (
          <div className="error-message">
            <p>Error: {error}</p>
          </div>
        )}

        {loading ? (
          <div className="loading">Loading coupons...</div>
        ) : (
          <>
            <div className="coupons-table-container">
              <table className="coupons-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Discount</th>
                    <th>Duration</th>
                    <th>Redemptions</th>
                    <th>Valid Until</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map((coupon) => (
                    <tr
                      key={coupon.id}
                      onClick={(e) => handleRowClick(coupon.id, e)}
                      className="coupon-row"
                    >
                      <td>
                        <strong>{coupon.name}</strong>
                        <br />
                        <small style={{ color: 'var(--text-secondary)' }}>
                          {coupon.stripe_coupon_id}
                        </small>
                      </td>
                      <td>
                        <span className="discount-badge">{formatDiscount(coupon)}</span>
                      </td>
                      <td>
                        <span className="duration-badge">{formatDuration(coupon)}</span>
                      </td>
                      <td>
                        {coupon.times_redeemed}
                        {coupon.max_redemptions && ` / ${coupon.max_redemptions}`}
                      </td>
                      <td>{formatDate(coupon.valid_until)}</td>
                      <td>
                        <span className={`status-badge ${coupon.is_active ? 'active' : 'inactive'}`}>
                          {coupon.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="actions">
                          <button
                            className="action-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/coupons/${coupon.id}`);
                            }}
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            className="action-btn danger"
                            onClick={(e) => handleDeleteClick(coupon, e)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {coupons.length === 0 && !loading && (
              <div className="loading">No coupons found</div>
            )}

            <div className="pagination">
              <div className="pagination-info">Showing {coupons.length} coupons</div>
              <div className="pagination-controls">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="btn-secondary"
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={currentPage >= totalPages}
                  className="btn-secondary"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showCreateModal && (
        <div className="modal-overlay" onClick={handleCloseCreateModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Coupon</h2>
              <button className="modal-close" onClick={handleCloseCreateModal}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit(onCreateCoupon)}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Coupon Name *</label>
                  <input
                    type="text"
                    {...register('name')}
                    placeholder="e.g., Summer Sale 20%"
                    className={errors.name ? 'input-error' : ''}
                  />
                  {errors.name && <span className="form-error">{errors.name.message}</span>}
                </div>

                <div className="form-group">
                  <label>Discount Type *</label>
                  <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        value="percent"
                        {...register('discountType')}
                      />
                      Percentage
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        value="amount"
                        {...register('discountType')}
                      />
                      Fixed Amount
                    </label>
                  </div>
                  {errors.discountType && <span className="form-error">{String(errors.discountType.message)}</span>}
                </div>

                {discountType === 'percent' ? (
                  <div className="form-group">
                    <label>Percent Off *</label>
                    <Controller
                      name="percent_off"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="number"
                          min="0.01"
                          max="100"
                          step="0.01"
                          value={field.value ?? ''}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                          placeholder="e.g., 20"
                          className={errors.percent_off ? 'input-error' : ''}
                        />
                      )}
                    />
                    <span className="form-hint">Enter a value between 0.01 and 100</span>
                    {errors.percent_off && <span className="form-error">{errors.percent_off.message}</span>}
                  </div>
                ) : (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Amount Off (cents) *</label>
                      <Controller
                        name="amount_off"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            min="1"
                            value={field.value ?? ''}
                            onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                            placeholder="e.g., 1000 for $10"
                            className={errors.amount_off ? 'input-error' : ''}
                          />
                        )}
                      />
                      <span className="form-hint">Amount in cents (1000 = $10.00)</span>
                      {errors.amount_off && <span className="form-error">{errors.amount_off.message}</span>}
                    </div>
                    <div className="form-group">
                      <label>Currency</label>
                      <select {...register('currency')}>
                        <option value="usd">USD</option>
                        <option value="eur">EUR</option>
                        <option value="gbp">GBP</option>
                      </select>
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label>Duration *</label>
                  <select {...register('duration')}>
                    <option value="once">Once</option>
                    <option value="repeating">Repeating</option>
                    <option value="forever">Forever</option>
                  </select>
                  {errors.duration && <span className="form-error">{String(errors.duration.message)}</span>}
                </div>

                {duration === 'repeating' && (
                  <div className="form-group">
                    <label>Duration in Months *</label>
                    <Controller
                      name="duration_in_months"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="number"
                          min="1"
                          value={field.value ?? ''}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                          placeholder="e.g., 3"
                          className={errors.duration_in_months ? 'input-error' : ''}
                        />
                      )}
                    />
                    {errors.duration_in_months && <span className="form-error">{errors.duration_in_months.message}</span>}
                  </div>
                )}

                <div className="form-group">
                  <label>Max Redemptions (optional)</label>
                  <Controller
                    name="max_redemptions"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="number"
                        min="1"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                        placeholder="Leave empty for unlimited"
                      />
                    )}
                  />
                </div>

                <div className="form-group">
                  <label>Valid Until (optional)</label>
                  <input
                    type="datetime-local"
                    {...register('valid_until')}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={handleCloseCreateModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={createLoading}
                >
                  {createLoading ? 'Creating...' : 'Create Coupon'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && couponToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Coupon</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="delete-warning">
                <p>Are you sure you want to delete this coupon?</p>
                <p>
                  <strong>{couponToDelete.name}</strong>
                </p>
                <p>This action will deactivate the coupon in Stripe and cannot be undone.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={confirmDelete}>
                Delete Coupon
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Coupons;

